import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  makeStyles, Container, Paper, Typography, Button, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Chip, Divider, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Tooltip, Grid
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(4) },
  paper: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  subtitle: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  tokenBox: {
    display: "flex", alignItems: "center", gap: 8,
    background: theme.palette.grey[100], borderRadius: 8, padding: "10px 16px",
    fontFamily: "monospace", fontSize: "0.9rem", wordBreak: "break-all",
    marginBottom: theme.spacing(1)
  },
  tokenText: { flex: 1 },
  divider: { margin: theme.spacing(3, 0) },
  formRow: { display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" },
  formControl: { minWidth: 180 },
  chip: { marginLeft: 4 },
  endpointBox: {
    background: theme.palette.grey[50], borderRadius: 8, padding: theme.spacing(2),
    fontFamily: "monospace", fontSize: "0.78rem", lineHeight: 1.8,
    border: `1px solid ${theme.palette.divider}`
  }
}));

const EVENTS = [
  { value: "contact.created",     label: "Contato criado" },
  { value: "ticket.created",      label: "Lead criado" },
  { value: "ticket.updated",      label: "Lead atualizado" },
  { value: "ticket.stage.changed", label: "Lead mudou de etapa" },
  { value: "message.sent",        label: "Mensagem enviada" },
  { value: "message.received",    label: "Mensagem recebida" },
];

const N8nIntegrations = () => {
  const classes = useStyles();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const [token, setToken] = useState("");
  const [loadingToken, setLoadingToken] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(true);

  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newEvent, setNewEvent] = useState("contact.created");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadToken();
    loadWebhooks();
  }, []);

  const loadToken = async () => {
    try {
      setLoadingToken(true);
      const { data } = await api.get("/public-api/token");
      setToken(data.apiToken);
    } catch (err) { toastError(err); }
    setLoadingToken(false);
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm("Regenerar o token vai invalidar o token atual. Todos os fluxos n8n precisarão ser atualizados. Continuar?")) return;
    try {
      setRegenerating(true);
      const { data } = await api.post("/public-api/token/regenerate");
      setToken(data.apiToken);
      toast.success("Token regenerado com sucesso!");
    } catch (err) { toastError(err); }
    setRegenerating(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const loadWebhooks = async () => {
    try {
      setLoadingWebhooks(true);
      const { data } = await api.get("/company-webhooks");
      setWebhooks(data);
    } catch (err) { toastError(err); }
    setLoadingWebhooks(false);
  };

  const handleAddWebhook = async () => {
    if (!newName || !newUrl || !newEvent) {
      toast.error("Preencha nome, URL e evento");
      return;
    }
    try {
      setSaving(true);
      await api.post("/company-webhooks", { name: newName, url: newUrl, event: newEvent });
      toast.success("Webhook adicionado!");
      setNewName(""); setNewUrl(""); setNewEvent("contact.created");
      loadWebhooks();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  const handleToggleWebhook = async (wh) => {
    try {
      await api.put(`/company-webhooks/${wh.id}`, { isActive: !wh.isActive });
      loadWebhooks();
    } catch (err) { toastError(err); }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm("Remover este webhook?")) return;
    try {
      await api.delete(`/company-webhooks/${id}`);
      toast.success("Webhook removido");
      loadWebhooks();
    } catch (err) { toastError(err); }
  };

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Typography variant="h5" className={classes.title}>Integrações / n8n</Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Conecte o Fenil CRM ao n8n usando seu token de API e webhooks.
      </Typography>

      {/* Token da API */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>Token da API</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Use este token para autenticar requisições à API pública.
          Passe via <code>?token=SEU_TOKEN</code> ou <code>Authorization: Bearer SEU_TOKEN</code>.
        </Typography>
        {loadingToken ? <CircularProgress size={20} /> : (
          <>
            <div className={classes.tokenBox}>
              <span className={classes.tokenText}>{token || "—"}</span>
              <Tooltip title="Copiar token">
                <IconButton size="small" onClick={() => handleCopy(token)}>
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <Button
              size="small" variant="outlined" startIcon={<RefreshIcon />}
              onClick={handleRegenerateToken} disabled={regenerating}
            >
              {regenerating ? "Regenerando..." : "Regenerar token"}
            </Button>
          </>
        )}

        <Divider className={classes.divider} />

        {/* Endpoints disponíveis */}
        <Typography variant="h6" gutterBottom>Endpoints disponíveis</Typography>
        <div className={classes.endpointBox}>
          <div><b>Base:</b> {backendUrl}</div>
          <br />
          <div>GET  {backendUrl}/public-api/stages — Etapas do Kanban</div>
          <div>GET  {backendUrl}/public-api/connections — Conexões WhatsApp da empresa</div>
          <div>GET  {backendUrl}/public-api/leads — Listar leads <small>(?page=1&amp;limit=20&amp;search=nome)</small></div>
          <div>GET  {backendUrl}/public-api/leads/:id — Buscar lead</div>
          <div>POST {backendUrl}/public-api/leads — Criar lead <small>&#123;name, phone, email?, stageId?, whatsappId?&#125;</small></div>
          <div>PUT  {backendUrl}/public-api/leads/:id — Atualizar lead <small>&#123;name?, email?, stageId?, status?, whatsappId?&#125;</small></div>
        </div>
        <Button
          size="small" variant="text" style={{ marginTop: 8 }}
          onClick={() => handleCopy(`${backendUrl}/public-api/leads?token=${token}`)}
        >
          Copiar URL de exemplo
        </Button>
      </Paper>

      {/* Webhooks */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>Webhooks de saída</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          O CRM vai enviar um POST automático para o n8n sempre que o evento ocorrer.
        </Typography>

        {/* Formulário de novo webhook */}
        <div className={classes.formRow} style={{ marginBottom: 16 }}>
          <TextField
            label="Nome" size="small" variant="outlined" value={newName}
            onChange={e => setNewName(e.target.value)} style={{ minWidth: 160 }}
          />
          <TextField
            label="URL do webhook (n8n)" size="small" variant="outlined" value={newUrl}
            onChange={e => setNewUrl(e.target.value)} style={{ minWidth: 320 }}
          />
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Evento</InputLabel>
            <Select value={newEvent} onChange={e => setNewEvent(e.target.value)} label="Evento">
              {EVENTS.map(ev => <MenuItem key={ev.value} value={ev.value}>{ev.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={handleAddWebhook} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Adicionar"}
          </Button>
        </div>

        {loadingWebhooks ? <CircularProgress size={24} /> : (
          webhooks.length === 0 ? (
            <Typography variant="body2" color="textSecondary">Nenhum webhook configurado.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks.map(wh => (
                  <TableRow key={wh.id}>
                    <TableCell>{wh.name}</TableCell>
                    <TableCell style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <Tooltip title={wh.url}><span>{wh.url}</span></Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={EVENTS.find(e => e.value === wh.event)?.label || wh.event} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={wh.isActive ? "Ativo" : "Inativo"}
                        color={wh.isActive ? "primary" : "default"}
                        onClick={() => handleToggleWebhook(wh)}
                        clickable
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleDeleteWebhook(wh.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </Paper>

      {/* Payload de exemplo */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>Payload do webhook (exemplo)</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Exemplo do evento <b>Lead criado</b> (ticket.created) — inclui conexão WhatsApp e etapa do Kanban:
        </Typography>
        <div className={classes.endpointBox}>
          {JSON.stringify({
            event: "ticket.created",
            timestamp: "2026-05-14T10:00:00.000Z",
            companyId: 1,
            data: {
              id: 9200,
              contactId: 5017,
              name: "João Silva",
              phone: "5511999999999",
              email: "joao@email.com",
              status: "pending",
              whatsappId: 3,
              connection: {
                id: 3,
                name: "Conexão Principal",
                status: "CONNECTED"
              },
              stage: null,
              lastMessage: "Lead criado via API",
              createdAt: "2026-05-14T10:00:00.000Z",
              updatedAt: "2026-05-14T10:00:00.000Z"
            }
          }, null, 2)}
        </div>
      </Paper>
    </Container>
  );
};

export default N8nIntegrations;
